import { LoadingSpinner, PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import React, { useEffect, useMemo, useState } from "react";
import { SmsIntegrationModel } from "../types";
import SmsIntegrationEditModal from "./SmsIntegrationEditModal";
import SmsIntegrationSetupModal from "./SmsIntegrationSetupModal";
import SmsConsentPanel from "./SmsConsentPanel";

interface SmsUsage {
  smsThisMonth: number;
  maxSmsPerMonth: number | null;
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>
  );
}

function numberTypeBadge(phoneNumber: string) {
  const digits = phoneNumber.replace(/\D/g, "");
  if (digits.length === 5 || digits.length === 6) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 ml-2">
        Short Code
      </span>
    );
  }
  if (phoneNumber.startsWith("+1800") || phoneNumber.startsWith("+1888") ||
      phoneNumber.startsWith("+1877") || phoneNumber.startsWith("+1866") ||
      phoneNumber.startsWith("+1855") || phoneNumber.startsWith("+1844") ||
      phoneNumber.startsWith("+1833")) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 ml-2">
        Toll-Free
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 ml-2">
      Local (10DLC)
    </span>
  );
}

export default function SmsIntegration() {
  const [loading, setLoading] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [integrations, setIntegrations] = useState<SmsIntegrationModel[]>([]);
  const [editedIntegrationId, setEditedIntegrationId] = useState<number>();
  const [editError, setEditError] = useState("");
  const [usage, setUsage] = useState<SmsUsage | null>(null);
  const [showBrandingGuide, setShowBrandingGuide] = useState(false);
  const api = useApi();

  const fetchIntegrations = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("Sms/Integrations");
      const data: SmsIntegrationModel[] = await response.json();
      setIntegrations(Array.isArray(data) ? data.filter((i) => !i.isIvr) : []);
    } catch {
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const fetchUsage = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("Sms/Integrations/Usage");
      const data = await response.json();
      setUsage({
        smsThisMonth: data.smsThisMonth ?? 0,
        maxSmsPerMonth: data.maxSmsPerMonth ?? null
      });
    } catch {
      setUsage(null);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchIntegrations();
    fetchUsage();
  }, [fetchIntegrations, fetchUsage]);

  const { getTeamNameById } = useTeams();

  const errorMap: Record<string, string> = {
    release_error: "An error occurred while releasing the phone number.",
    update_error: "An error occurred while updating the phone number."
  };

  const editedIntegration = useMemo(() => {
    return integrations.find((x) => x.id === editedIntegrationId);
  }, [editedIntegrationId, integrations]);

  async function release() {
    setLoading(true);
    if (window.confirm("Are you sure you want to release this phone number? This cannot be undone.")) {
      try {
        setIntegrations(integrations.filter((i) => i.id !== editedIntegrationId));
        const resp = await api.messaging.delete(`Sms/Integrations/${editedIntegrationId}`);
        if (resp.ok) {
          setEditedIntegrationId(undefined);
          setEditError("");
        }
      } catch {
        setEditError(errorMap["release_error"]);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }

  async function update(integration: SmsIntegrationModel) {
    setLoading(true);
    try {
      const newIntegrations = [...integrations];
      const index = integrations.findIndex((x) => x.id === editedIntegrationId);
      newIntegrations[index] = integration;
      setIntegrations(newIntegrations);

      const resp = await api.messaging.post("Sms/Integrations/UpdatePhoneNumber", {
        Id: editedIntegrationId,
        TeamId: integration.teamId,
        WorkflowId: integration.workflowId ?? null
      });
      if (resp.ok) {
        setEditedIntegrationId(undefined);
        setEditError("");
      } else {
        setEditError(errorMap["update_error"]);
      }
    } catch {
      setEditError(errorMap["update_error"]);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 text-white">
          <PhoneIcon />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">SMS / Text Messaging</h2>
          <p className="text-sm text-slate-500">
            Provision or port phone numbers to send and receive SMS in the Velaro inbox. Powered by
            Twilio. Supports local 10DLC numbers, toll-free numbers, and short codes.
          </p>
        </div>
      </div>

      {/* 10DLC compliance info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <i className="fas fa-triangle-exclamation text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="text-sm text-amber-800">
          <span className="font-semibold">10DLC Compliance:</span> US carriers require all local
          10DLC numbers to be registered for A2P (Application-to-Person) messaging. Velaro handles
          brand and campaign registration automatically when you provision a number through this page.
          Unregistered messages will be blocked or filtered starting in 2024.
        </div>
      </div>

      {/* Branded sender ID guide */}
      <div className="border border-blue-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowBrandingGuide(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
          aria-expanded={showBrandingGuide}
        >
          <div className="flex items-center gap-2">
            <i className="fas fa-id-badge text-blue-500 text-sm" aria-hidden="true" />
            <span className="text-sm font-semibold text-blue-900">Brand your SMS — show your logo on iPhone & Android</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Free</span>
          </div>
          <i className={`fas fa-chevron-${showBrandingGuide ? "up" : "down"} text-blue-400 text-xs`} aria-hidden="true" />
        </button>
        {showBrandingGuide && (
          <div className="bg-white px-4 py-4 space-y-4">
            <p className="text-xs text-slate-600">
              Register your sending number(s) with Apple and Google so recipients see your business name,
              logo, and verified badge instead of a raw phone number. Both programs are free — approval
              typically takes 1–3 business days.
            </p>

            {/* Numbers to register */}
            {integrations.length > 0 && (
              <div className="bg-slate-50 rounded border border-slate-200 px-3 py-2">
                <p className="text-xs font-medium text-slate-600 mb-1">Your sending numbers to register:</p>
                <div className="flex flex-wrap gap-2">
                  {integrations.map(i => (
                    <code key={i.id} className="text-xs bg-white border border-slate-200 px-2 py-0.5 rounded font-mono">{i.phoneNumber}</code>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* Apple */}
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fab fa-apple text-slate-700" aria-hidden="true" />
                  <span className="text-xs font-semibold text-slate-800">iPhone — Apple Business Register</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Creates the branded sender card (logo, name, verified badge) shown when your number
                  texts anyone on iOS. Register once per number. Requires an Apple Business account.
                </p>
                <ol className="text-xs text-slate-600 space-y-1 mb-3 list-decimal list-inside">
                  <li>Sign in at <span className="font-medium">register.apple.com</span></li>
                  <li>Go to <span className="font-medium">Phone Numbers → Add</span></li>
                  <li>Enter your sending number — Apple sends a verification code</li>
                  <li>Upload your logo (1024×1024 PNG) in your business profile</li>
                  <li>Submit for review — approved within 1–3 business days</li>
                </ol>
                <a
                  href="https://register.apple.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium hover:underline"
                >
                  Open Apple Business Register
                  <i className="fas fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                </a>
              </div>

              {/* Google RCS */}
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fab fa-google text-slate-700" aria-hidden="true" />
                  <span className="text-xs font-semibold text-slate-800">Android — Google RCS Business Messaging</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Shows your logo, brand color, and verified checkmark on Android devices that support
                  RCS. Managed through Google's Business Communications console. Requires Google Cloud
                  project.
                </p>
                <ol className="text-xs text-slate-600 space-y-1 mb-3 list-decimal list-inside">
                  <li>Go to <span className="font-medium">business.google.com/rcs</span></li>
                  <li>Create a brand profile — logo, color, website, description</li>
                  <li>Register your sending number(s) under the brand</li>
                  <li>Submit for verification — Google reviews the brand claim</li>
                  <li>Once verified, use the <span className="font-medium">RCS</span> tab in this panel to connect it</li>
                </ol>
                <a
                  href="https://business.google.com/rcs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium hover:underline"
                >
                  Open RCS Business Console
                  <i className="fas fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                </a>
              </div>
            </div>

            <p className="text-xs text-slate-400">
              Need help? Contact{" "}
              <a href="mailto:support@velaro.com" className="text-blue-500 hover:underline">support@velaro.com</a>
              {" "}— we can walk you through the registration for your numbers.
            </p>
          </div>
        )}
      </div>

      {/* Usage quota bar */}
      {usage && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">SMS conversations this month</span>
            <span className="text-sm text-slate-500">
              {usage.smsThisMonth.toLocaleString()}
              {usage.maxSmsPerMonth ? ` / ${usage.maxSmsPerMonth.toLocaleString()}` : " (unlimited)"}
            </span>
          </div>
          {usage.maxSmsPerMonth ? (
            <>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    usage.smsThisMonth / usage.maxSmsPerMonth >= 0.9
                      ? "bg-red-500"
                      : usage.smsThisMonth / usage.maxSmsPerMonth >= 0.7
                      ? "bg-amber-400"
                      : "bg-emerald-500"
                  }`}
                  style={{
                    width: `${Math.min(100, (usage.smsThisMonth / usage.maxSmsPerMonth) * 100)}%`
                  }}
                />
              </div>
              {usage.smsThisMonth >= usage.maxSmsPerMonth && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  Monthly limit reached — new conversations are blocked until next month or your plan is upgraded.
                </p>
              )}
            </>
          ) : (
            <div className="w-full bg-emerald-100 rounded-full h-2">
              <div className="h-2 w-1/4 rounded-full bg-emerald-400" />
            </div>
          )}
        </div>
      )}

      {/* Connected numbers */}
      {integrations.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-slate-200 rounded-lg text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Phone Number</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Team</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Workflow</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600" style={{ width: 1 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {integrations.map((integration) => (
                <tr key={integration.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center flex-wrap gap-1">
                      <span className="font-medium text-slate-800 font-mono">
                        {integration.phoneNumber}
                      </span>
                      {numberTypeBadge(integration.phoneNumber)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {getTeamNameById(Number(integration.teamId)) || "All teams"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {integration.workflowName ? (
                      <span className="inline-flex items-center gap-1">
                        <i className="fas fa-diagram-project text-blue-400" aria-hidden="true" />
                        {integration.workflowName}
                        {integration.workflowEnabled === false && (
                          <span className="text-amber-600 font-medium">(disabled)</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Active
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      onClick={() => setEditedIntegrationId(integration.id)}
                      aria-label={`Edit ${integration.phoneNumber}`}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {integrations.length === 0 && !loading && (
        <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 text-emerald-600">
            <PhoneIcon />
          </div>
          <p className="text-slate-600 font-medium">No SMS numbers connected</p>
          <p className="text-slate-400 text-sm mt-1">
            Add a phone number to start sending and receiving SMS in your inbox.
          </p>
        </div>
      )}

      {/* Add button */}
      <div>
        <PrimaryBlueButton
          onClick={() => setShowSetupModal(true)}
          label="Add Phone Number"
        />
        <p className="text-xs text-slate-400 mt-2">
          Buy a new local or toll-free number, port an existing number, or learn about short codes.
        </p>
      </div>

      {/* Opt-in Settings + Consent Audit Log */}
      <SmsConsentPanel />

      {showSetupModal && (
        <SmsIntegrationSetupModal
          onSuccess={() => {
            setShowSetupModal(false);
            fetchIntegrations();
          }}
          onClose={() => setShowSetupModal(false)}
        />
      )}

      {editedIntegration && (
        <SmsIntegrationEditModal
          onClose={() => {
            setEditedIntegrationId(undefined);
            setEditError("");
          }}
          release={release}
          update={update}
          integration={editedIntegration}
          error={editError}
        />
      )}
    </div>
  );
}
